import {
  formatNote,
  readNote,
  startsWithLatinNote,
  type FormatOptions,
  type NoteName,
  type Notation,
} from './notes'

export interface ParsedChord {
  kind: 'chord'
  root: NoteName
  /** Calidad y extensiones tal como se escribieron: «m», «7», «maj7», «sus4», «m7b5»… */
  suffix: string
  bass?: NoteName
  notation: Notation
}

export interface NoChord {
  kind: 'nc'
}

export type ChordSymbol = ParsedChord | NoChord

const NO_CHORD = /^(N\.?C\.?|n\.?c\.?)$/

/**
 * Piezas válidas del sufijo de un acorde. Se prueban en orden, así que las largas van primero.
 * Si el sufijo entero no se puede trocear con ellas, el acorde no se reconoce y queda «por revisar».
 */
const SUFFIX_TOKENS: readonly string[] = [
  'maj',
  'Maj',
  'MAJ',
  'min',
  'dim',
  'aug',
  'aum',
  'sus',
  'add',
  'omit',
  'no',
  'alt',
  'm',
  'M',
  '-',
  '+',
  '°',
  'º',
  'o',
  'ø',
  'Δ',
  '#',
  '♯',
  'b',
  '♭',
  '(',
  ')',
  ',',
  '/',
]

export function isValidSuffix(suffix: string): boolean {
  let index = 0
  let depth = 0
  while (index < suffix.length) {
    const digits = /^\d+/.exec(suffix.slice(index))
    if (digits) {
      index += digits[0].length
      continue
    }
    const token = SUFFIX_TOKENS.find((candidate) => suffix.startsWith(candidate, index))
    if (!token) return false
    if (token === '(') depth += 1
    if (token === ')') depth -= 1
    if (depth < 0) return false
    index += token.length
  }
  return depth === 0
}

/** Notación de un símbolo aislado, o null si no se reconoce como acorde. */
export function detectChordNotation(symbol: string): Notation | null {
  const parsed = parseChord(symbol)
  return parsed?.kind === 'chord' ? parsed.notation : null
}

/**
 * Interpreta un acorde en notación americana (C, Dm, G7, D/F#) o latina (Do, Rem, Sol7, Re/Fa#).
 * Devuelve null si no lo reconoce: quien llama lo marca «por revisar» y no lo descarta.
 *
 * En modo automático se prueba primero la latina si el símbolo empieza por un nombre latino:
 * «Do» es Do (y no D disminuido), pero «Faug» no es «Fa» + «ug», así que cae en americana.
 */
export function parseChord(
  symbol: string,
  notation: Notation | 'auto' = 'auto',
): ChordSymbol | null {
  const input = symbol.trim()
  if (input === '') return null
  if (NO_CHORD.test(input)) return { kind: 'nc' }
  const candidates: Notation[] =
    notation !== 'auto'
      ? [notation]
      : startsWithLatinNote(input)
        ? ['latin', 'american']
        : ['american']
  for (const candidate of candidates) {
    const chord = parseIn(input, candidate)
    if (chord) return chord
  }
  return null
}

function parseIn(input: string, notation: Notation): ParsedChord | null {
  const root = readNote(input, 0, notation)
  if (!root) return null

  // El bajo alternativo es lo que va tras la última «/», si es una nota. «6/9» no es un bajo.
  let body = input.slice(root.length)
  let bass: NoteName | undefined
  const slash = body.lastIndexOf('/')
  if (slash >= 0) {
    const candidate = body.slice(slash + 1)
    const bassNote = readNote(candidate, 0, notation)
    if (bassNote && bassNote.length === candidate.length) {
      bass = bassNote.note
      body = body.slice(0, slash)
    }
  }

  if (!isValidSuffix(body)) return null
  const chord: ParsedChord = { kind: 'chord', root: root.note, suffix: body, notation }
  if (bass) chord.bass = bass
  return chord
}

function formatSuffix(suffix: string, options: FormatOptions): string {
  if (!options.unicode) return suffix
  return suffix.replace(
    /([#b])(\d)/g,
    (_, accidental: string, digit: string) => (accidental === '#' ? '♯' : '♭') + digit,
  )
}

/** Escribe un acorde en la notación pedida. «Rem» se muestra «Dm» en americana. */
export function formatChord(
  chord: ChordSymbol,
  notation: Notation,
  options: FormatOptions = {},
): string {
  if (chord.kind === 'nc') return 'N.C.'
  const bass = chord.bass ? `/${formatNote(chord.bass, notation, options)}` : ''
  return formatNote(chord.root, notation, options) + formatSuffix(chord.suffix, options) + bass
}

/** Muestra un símbolo en la notación de la pantalla, o lo deja como está si no se reconoce. */
export function displayChord(
  symbol: string,
  notation: Notation,
  options: FormatOptions = {},
): string {
  const parsed = parseChord(symbol)
  return parsed ? formatChord(parsed, notation, options) : symbol
}

/** Notación mayoritaria de una lista de acordes. Ante empate o lista vacía, americana. */
export function detectNotation(symbols: readonly string[]): Notation {
  let latin = 0
  let american = 0
  for (const symbol of symbols) {
    const notation = detectChordNotation(symbol)
    if (notation === 'latin') latin += 1
    else if (notation === 'american') american += 1
  }
  return latin > american ? 'latin' : 'american'
}
